import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Paper,
  Divider,
  Button,
  CircularProgress,
  Alert,
  Chip,
} from "@mui/material";
import { delftBlue } from "@/theme/colors";
import { useGetPrestartFormByIdQuery } from "@/app/features/dashboard/dashboardApiSlice";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useSelector } from "react-redux";
import { selectUser } from "@/app/features/auth/authSlice";
import Roles from "@/constants/roles";
import PrestartFormVersionPanel from "./components/PrestartFormVersionPanel";

const PrestartFormViewPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector(selectUser);
  const isSuperAdmin = user?.role === Roles.SUPER_ADMIN;
  const initialVersion = location.state?.templateVersion
    ? String(location.state.templateVersion)
    : "latest";
  const [selectedVersion, setSelectedVersion] = useState(initialVersion);

  const {
    data: formResponse,
    isLoading,
    error,
  } = useGetPrestartFormByIdQuery({
    id,
    version: selectedVersion === "latest" ? undefined : selectedVersion,
  });

  const form = formResponse?.data?.prestartForm;

  const renderQuestion = (field, index) => {
    if (!field) return null;

    // Handle different field structures - some have 'question', some have 'label'
    let questionText = field.question || field.label || `Question ${index + 1}`;
    
    // If questionText is an object, extract the string value
    if (typeof questionText === 'object' && questionText !== null) {
      questionText = questionText.label || questionText.value || questionText.text || `Question ${index + 1}`;
    }
    
    // Ensure it's a string
    const displayText = String(questionText || `Question ${index + 1}`);
    
    const fieldType = field.type || "text";
    const isRequired = field.validation?.required || field.required || false;
    const showNotes = field.showNotesInput !== false;
    const notePlaceholder = field.notePlaceholder || "(Optional) Notes";
    const noteLabel = field.noteLabel;

    return (
      <Box
        key={field.id || field.fieldId || index}
        sx={{
          marginBottom: 3,
          padding: { xs: 2, sm: 2.5 },
          background: "white",
          borderRadius: 2,
          outline: `1px ${delftBlue[100]} solid`,
          outlineOffset: "-1px",
        }}
      >
        <Stack spacing={1.5}>
          {/* Question Text */}
          <Box>
            <Typography
              sx={{
                color: delftBlue[500],
                fontSize: { xs: 14, sm: 15, md: 16 },
                fontFamily: "Montserrat, sans-serif",
                fontWeight: 600,
                marginBottom: 0.5,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              {displayText}
              {isRequired && (
                <Chip
                  label="Required"
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: 10,
                    fontFamily: "Montserrat, sans-serif",
                    backgroundColor: delftBlue[100],
                    color: delftBlue[700],
                  }}
                />
              )}
            </Typography>
          </Box>

          {/* Question Type Display */}
          <Box>
            {fieldType === "yes-no-question" && (
              <Stack direction="row" spacing={2} alignItems="center">
                <Chip
                  label="Yes"
                  size="small"
                  sx={{
                    backgroundColor: delftBlue[100],
                    color: delftBlue[700],
                    fontFamily: "Montserrat, sans-serif",
                  }}
                />
                <Chip
                  label="No"
                  size="small"
                  sx={{
                    backgroundColor: delftBlue[100],
                    color: delftBlue[700],
                    fontFamily: "Montserrat, sans-serif",
                  }}
                />
                {showNotes && (
                  <Typography
                    sx={{
                      color: delftBlue[400],
                      fontSize: { xs: 12, sm: 13 },
                      fontFamily: "Montserrat, sans-serif",
                      fontStyle: "italic",
                    }}
                  >
                    {notePlaceholder}
                  </Typography>
                )}
              </Stack>
            )}

            {(fieldType === "text" || fieldType === "textarea") && (
              <Box
                sx={{
                  padding: 1.5,
                  borderRadius: 1,
                  backgroundColor: delftBlue[50],
                  border: `1px dashed ${delftBlue[200]}`,
                }}
              >
                <Typography
                  sx={{
                    color: delftBlue[400],
                    fontSize: { xs: 12, sm: 13 },
                    fontFamily: "Montserrat, sans-serif",
                    fontStyle: "italic",
                  }}
                >
                  {field.placeholder || "Text input field"}
                </Typography>
              </Box>
            )}

            {(fieldType === "select" || fieldType === "dropdown") && (
              <Box>
                <Typography
                  sx={{
                    color: delftBlue[500],
                    fontSize: { xs: 12, sm: 13 },
                    fontFamily: "Montserrat, sans-serif",
                    fontWeight: 500,
                    marginBottom: 1,
                  }}
                >
                  Options:
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                  {field.options && field.options.length > 0 ? (
                    field.options.map((option, optIndex) => {
                      // Handle options that might be objects
                      const optionLabel = typeof option === 'string' 
                        ? option 
                        : (option?.label || option?.value || option?.text || `Option ${optIndex + 1}`);
                      return (
                        <Chip
                          key={optIndex}
                          label={optionLabel}
                          size="small"
                          sx={{
                            backgroundColor: delftBlue[100],
                            color: delftBlue[700],
                            fontFamily: "Montserrat, sans-serif",
                          }}
                        />
                      );
                    })
                  ) : (
                    <Typography
                      sx={{
                        color: delftBlue[400],
                        fontSize: { xs: 12, sm: 13 },
                        fontFamily: "Montserrat, sans-serif",
                        fontStyle: "italic",
                      }}
                    >
                      No options specified
                    </Typography>
                  )}
                </Stack>
              </Box>
            )}

            {fieldType === "checkbox" && (
              <Box>
                <Typography
                  sx={{
                    color: delftBlue[500],
                    fontSize: { xs: 12, sm: 13 },
                    fontFamily: "Montserrat, sans-serif",
                    fontWeight: 500,
                    marginBottom: 1,
                  }}
                >
                  Options:
                </Typography>
                <Stack spacing={1}>
                  {field.options && field.options.length > 0 ? (
                    field.options.map((option, optIndex) => {
                      // Handle options that might be objects
                      const optionLabel = typeof option === 'string' 
                        ? option 
                        : (option?.label || option?.value || option?.text || `Option ${optIndex + 1}`);
                      return (
                        <Box
                          key={optIndex}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Box
                            sx={{
                              width: 16,
                              height: 16,
                              borderRadius: 0.5,
                              border: `2px solid ${delftBlue[300]}`,
                              backgroundColor: "white",
                            }}
                          />
                          <Typography
                            sx={{
                              color: delftBlue[700],
                              fontSize: { xs: 13, sm: 14 },
                              fontFamily: "Montserrat, sans-serif",
                            }}
                          >
                            {optionLabel}
                          </Typography>
                        </Box>
                      );
                    })
                  ) : (
                    <Typography
                      sx={{
                        color: delftBlue[400],
                        fontSize: { xs: 12, sm: 13 },
                        fontFamily: "Montserrat, sans-serif",
                        fontStyle: "italic",
                      }}
                    >
                      No options specified
                    </Typography>
                  )}
                </Stack>
              </Box>
            )}

            {fieldType === "radio" && (
              <Box>
                <Typography
                  sx={{
                    color: delftBlue[500],
                    fontSize: { xs: 12, sm: 13 },
                    fontFamily: "Montserrat, sans-serif",
                    fontWeight: 500,
                    marginBottom: 1,
                  }}
                >
                  Options:
                </Typography>
                <Stack spacing={1}>
                  {field.options && field.options.length > 0 ? (
                    field.options.map((option, optIndex) => {
                      // Handle options that might be objects
                      const optionLabel = typeof option === 'string' 
                        ? option 
                        : (option?.label || option?.value || option?.text || `Option ${optIndex + 1}`);
                      return (
                        <Box
                          key={optIndex}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Box
                            sx={{
                              width: 16,
                              height: 16,
                              borderRadius: "50%",
                              border: `2px solid ${delftBlue[300]}`,
                              backgroundColor: "white",
                            }}
                          />
                          <Typography
                            sx={{
                              color: delftBlue[700],
                              fontSize: { xs: 13, sm: 14 },
                              fontFamily: "Montserrat, sans-serif",
                            }}
                          >
                            {optionLabel}
                          </Typography>
                        </Box>
                      );
                    })
                  ) : (
                    <Typography
                      sx={{
                        color: delftBlue[400],
                        fontSize: { xs: 12, sm: 13 },
                        fontFamily: "Montserrat, sans-serif",
                        fontStyle: "italic",
                      }}
                    >
                      No options specified
                    </Typography>
                  )}
                </Stack>
              </Box>
            )}

            {fieldType === "file" && (
              <Box
                sx={{
                  padding: 1.5,
                  borderRadius: 1,
                  backgroundColor: delftBlue[50],
                  border: `1px dashed ${delftBlue[200]}`,
                }}
              >
                <Typography
                  sx={{
                    color: delftBlue[400],
                    fontSize: { xs: 12, sm: 13 },
                    fontFamily: "Montserrat, sans-serif",
                    fontStyle: "italic",
                  }}
                >
                  File upload field
                </Typography>
              </Box>
            )}

            {(fieldType === "date" ||
              fieldType === "time" ||
              fieldType === "datetime") && (
              <Box
                sx={{
                  padding: 1.5,
                  borderRadius: 1,
                  backgroundColor: delftBlue[50],
                  border: `1px dashed ${delftBlue[200]}`,
                }}
              >
                <Typography
                  sx={{
                    color: delftBlue[400],
                    fontSize: { xs: 12, sm: 13 },
                    fontFamily: "Montserrat, sans-serif",
                    fontStyle: "italic",
                  }}
                >
                  {fieldType === "date"
                    ? "Date picker"
                    : fieldType === "time"
                    ? "Time picker"
                    : "Date & Time picker"}
                </Typography>
              </Box>
            )}

            {noteLabel && (
              <Typography
                sx={{
                  color: delftBlue[600],
                  fontSize: { xs: 11, sm: 12 },
                  fontFamily: "Montserrat, sans-serif",
                  fontStyle: "italic",
                  marginTop: 1,
                }}
              >
                {noteLabel}
              </Typography>
            )}
          </Box>
        </Stack>
      </Box>
    );
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          padding: 3,
        }}
      >
        <Alert severity="error">
          {error?.data?.message || "Failed to load form template"}
        </Alert>
      </Box>
    );
  }

  if (!form) {
    return (
      <Box
        sx={{
          padding: 3,
        }}
      >
        <Alert severity="info">Form template not found</Alert>
      </Box>
    );
  }

  // Extract questions from formConfig
  const formConfig = form.formConfig || {};
  const pages = formConfig.pages || [];

  return (
    <Box
      component="main"
      sx={{
        flexGrow: 1,
        alignSelf: "stretch",
        minHeight: "100vh",
        paddingTop: { xs: 2, sm: 2.5, md: 3 },
        paddingLeft: { xs: 1.5, sm: 2, md: 3 },
        paddingRight: { xs: 1.5, sm: 2, md: 3 },
        paddingBottom: { xs: 2, sm: 2.5, md: 3 },
      }}
    >
      <Stack spacing={{ xs: 2, sm: 2.5, md: 3 }} sx={{ alignSelf: "stretch" }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            marginBottom: 1,
          }}
        >
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/forms")}
            sx={{
              color: delftBlue[500],
              textTransform: "none",
              fontFamily: "Montserrat, sans-serif",
            }}
          >
            Back
          </Button>
        </Box>

        {/* Form Info */}
        <Paper
          sx={{
            padding: { xs: 2, sm: 3 },
            borderRadius: 2,
            outline: `1px ${delftBlue[100]} solid`,
          }}
        >
          <Stack spacing={2}>
            <Box>
              <Typography
                sx={{
                  color: delftBlue[500],
                  fontSize: { xs: 22, sm: 24, md: 28 },
                  fontFamily: "Audiowide, sans-serif",
                  fontWeight: 400,
                  marginBottom: 1,
                }}
              >
                {isSuperAdmin
                  ? form.name
                  : form.versionTitle?.trim() || form.activityId?.name || form.name}
              </Typography>
              <Typography
                sx={{
                  color: delftBlue[500],
                  fontSize: { xs: 13, sm: 14, md: 16 },
                  fontFamily: "Montserrat, sans-serif",
                  fontWeight: 400,
                }}
              >
                {form.description}
              </Typography>
            </Box>
            <Divider />
            <Stack direction="row" spacing={2} flexWrap="wrap" gap={1}>
              <Box>
                <Typography
                  sx={{
                    color: delftBlue[400],
                    fontSize: { xs: 12, sm: 13 },
                    fontFamily: "Montserrat, sans-serif",
                    fontWeight: 500,
                  }}
                >
                  Activity:
                </Typography>
                <Typography
                  sx={{
                    color: delftBlue[700],
                    fontSize: { xs: 13, sm: 14 },
                    fontFamily: "Montserrat, sans-serif",
                  }}
                >
                  {form.activityId?.name || "N/A"}
                </Typography>
              </Box>
              <Box>
                <Typography
                  sx={{
                    color: delftBlue[400],
                    fontSize: { xs: 12, sm: 13 },
                    fontFamily: "Montserrat, sans-serif",
                    fontWeight: 500,
                  }}
                >
                  Status:
                </Typography>
                <Chip
                  label={form.isActive ? "Active" : "Inactive"}
                  size="small"
                  color={form.isActive ? "success" : "default"}
                  sx={{
                    fontFamily: "Montserrat, sans-serif",
                  }}
                />
              </Box>
              <Box>
                <Typography
                  sx={{
                    color: delftBlue[400],
                    fontSize: { xs: 12, sm: 13 },
                    fontFamily: "Montserrat, sans-serif",
                    fontWeight: 500,
                  }}
                >
                  Version:
                </Typography>
                <Chip
                  label={
                    isSuperAdmin
                      ? `v${form.templateVersion || form.latestVersion || 1}${
                          form.latestVersion && form.templateVersion !== form.latestVersion
                            ? ` / latest v${form.latestVersion}`
                            : ""
                        }`
                      : `v${form.templateVersion || 1}`
                  }
                  size="small"
                  sx={{ fontFamily: "Montserrat, sans-serif" }}
                />
              </Box>
            </Stack>
          </Stack>
        </Paper>

        {isSuperAdmin && (
          <PrestartFormVersionPanel
            formId={form.id || form._id || id}
            form={form}
            isSuperAdmin={isSuperAdmin}
            selectedVersion={selectedVersion}
            onSelectVersion={setSelectedVersion}
          />
        )}

        {/* Questions by Page */}
        {!isSuperAdmin && (pages.length > 0 ? (
          pages.map((page, pageIndex) => (
            <Paper
              key={pageIndex}
              sx={{
                padding: { xs: 2, sm: 3 },
                borderRadius: 2,
                outline: `1px ${delftBlue[100]} solid`,
              }}
            >
              <Stack spacing={2}>
                <Box>
                  <Typography
                    sx={{
                      color: delftBlue[500],
                      fontSize: { xs: 18, sm: 20, md: 22 },
                      fontFamily: "Audiowide, sans-serif",
                      fontWeight: 400,
                      marginBottom: 0.5,
                    }}
                  >
                    {page.title || `Page ${page.pageNumber || pageIndex + 1}`}
                  </Typography>
                  {page.subtitle && (
                    <Typography
                      sx={{
                        color: delftBlue[400],
                        fontSize: { xs: 13, sm: 14 },
                        fontFamily: "Montserrat, sans-serif",
                      }}
                    >
                      {page.subtitle}
                    </Typography>
                  )}
                </Box>
                <Divider />

                {/* Sections */}
                {page.sections && page.sections.length > 0 ? (
                  page.sections.map((section, sectionIndex) => (
                    <Box key={sectionIndex}>
                      {section.title && (
                        <Typography
                          sx={{
                            color: delftBlue[600],
                            fontSize: { xs: 16, sm: 18 },
                            fontFamily: "Montserrat, sans-serif",
                            fontWeight: 600,
                            marginBottom: 2,
                          }}
                        >
                          {section.title}
                        </Typography>
                      )}

                      {/* Questions/Fields */}
                      {section.fields && section.fields.length > 0 ? (
                        <Stack spacing={2}>
                          {section.fields.map((field, fieldIndex) =>
                            renderQuestion(field, fieldIndex)
                          )}
                        </Stack>
                      ) : (
                        <Typography
                          sx={{
                            color: delftBlue[400],
                            fontSize: { xs: 13, sm: 14 },
                            fontFamily: "Montserrat, sans-serif",
                            fontStyle: "italic",
                          }}
                        >
                          No questions in this section
                        </Typography>
                      )}
                    </Box>
                  ))
                ) : (
                  <Typography
                    sx={{
                      color: delftBlue[400],
                      fontSize: { xs: 13, sm: 14 },
                      fontFamily: "Montserrat, sans-serif",
                      fontStyle: "italic",
                    }}
                  >
                    No sections in this page
                  </Typography>
                )}
              </Stack>
            </Paper>
          ))
        ) : form.fields && form.fields.length > 0 ? (
          // Fallback to flat fields structure if formConfig is not available
          <Paper
            sx={{
              padding: { xs: 2, sm: 3 },
              borderRadius: 2,
              outline: `1px ${delftBlue[100]} solid`,
            }}
          >
            <Stack spacing={2}>
              <Typography
                sx={{
                  color: delftBlue[500],
                  fontSize: { xs: 18, sm: 20, md: 22 },
                  fontFamily: "Audiowide, sans-serif",
                  fontWeight: 400,
                  marginBottom: 1,
                }}
              >
                Form Questions
              </Typography>
              <Divider />
              <Stack spacing={2}>
                {form.fields.map((field, index) =>
                  renderQuestion(field, index)
                )}
              </Stack>
            </Stack>
          </Paper>
        ) : (
          <Paper
            sx={{
              padding: { xs: 2, sm: 3 },
              borderRadius: 2,
              outline: `1px ${delftBlue[100]} solid`,
            }}
          >
            <Typography
              sx={{
                color: delftBlue[400],
                fontSize: { xs: 13, sm: 14 },
                fontFamily: "Montserrat, sans-serif",
                fontStyle: "italic",
                textAlign: "center",
              }}
            >
              No questions configured for this form
            </Typography>
          </Paper>
        ))}
      </Stack>
    </Box>
  );
};

export default PrestartFormViewPage;

